import type { RecordResult } from '../@types/records';
import type { RiskMatrixDataResponse, RiskMatrixRatingValue } from '../@types/risk-matrix-field';

export interface RiskRatingGroup {
  displayValue: string;
  count: number;
  riskLevelId: number;
}
/**
 * Get the background colour attributes for a risk rating cell
 * @param data - Array of risk matrix data responses
 * @param id - The risk rating ID to find colors for
 * @returns Object with color and backgroundColor properties
 */
export const getRiskRatingCellAttributes = (data: RiskMatrixDataResponse[], id?: number) => {
  const element = data.find((item) => item.id === id);
  return {
    color: element?.riskLevel?.textColor || 'inherit',
    backgroundColor: element?.riskLevel?.backgroundColor || 'transparent',
  };
};

/**
 * Extract and group risk rating values from linked records
 */
export const extractRiskRatingGroups = (records: RecordResult[], linkedFieldId: number): RiskRatingGroup[] => {
  const riskRatingData = records.reduce<RiskMatrixRatingValue[]>((acc, record) => {
    const fieldData = record?.fields?.[linkedFieldId] as RiskMatrixRatingValue | undefined;
    if (fieldData) {
      acc.push(fieldData);
    }
    return acc;
  }, []);

  if (riskRatingData.length === 0) {
    return [];
  }

  const groupMap: Map<string, RiskRatingGroup> = new Map();

  riskRatingData.forEach((data) => {
    const riskLevelId = typeof data.id === 'number' ? data.id : Number(data.id);
    if (isNaN(riskLevelId) || riskLevelId <= 0) return;

    const key = data.displayValue || '';
    if (groupMap.has(key)) {
      const existing = groupMap.get(key)!;
      existing.count += 1;
    } else {
      groupMap.set(key, {
        displayValue: key,
        count: 1,
        riskLevelId,
      });
    }
  });

  return Array.from(groupMap.values()).sort((a, b) => a.displayValue.localeCompare(b.displayValue));
};

export const formatSegmentText = (group: RiskRatingGroup) =>
  group.count > 1 ? `${group.displayValue} (x${group.count})` : group.displayValue;

export const extractFirstRiskMatrixId = (records: RecordResult[], linkedFieldId: number): number | undefined => {
  for (const record of records) {
    const riskRating = record.fields?.[linkedFieldId] as RiskMatrixRatingValue | undefined;
    if (riskRating?.riskMatrixId) {
      return riskRating.riskMatrixId;
    }
  }
  return undefined;
};
